import React, { useState } from 'react';

import { SubmitHandler, useForm } from 'react-hook-form';

import { isPasswordSecure } from '../../../services/auth/utils';
import Button from '../../Button';
import CollapsableNotification from '../../CollapsableNotification';
import {
  ButtonContainer,
  FormContainer, GuideBody, GuideTextContainer, GuideTitle, StyledTextField,
} from '../index.styles';

interface FormValues {
  password: string,
}

interface NewPasswordFormProps {
  onFormSubmit: (newPassword: string) => void;
}

const NewPasswordForm = ({ onFormSubmit }: NewPasswordFormProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();
  const [errorOpen, setErrorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onSuccess = () => {
    setLoading(false);
  };

  const onFailure = (err: any) => {
    setLoading(false);
    setErrorMessage(err.message || 'An error has occurred');
    setErrorOpen(true);
  };

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    setLoading(true);
    if (isPasswordSecure(data?.password)) {
      try {
        await onFormSubmit(data.password);
        onSuccess();
      } catch (err) {
        onFailure(err);
      }
    } else {
      onFailure({ message: 'Password must be at least 8 character long with a letter and a number' });
    }
  };

  const getErrorMessage = (type: string | undefined, field: string): string => {
    switch (type) {
      case 'pattern':
        return `Please enter a valid ${field}`;
      case 'required':
        return `Please enter your ${field}`;
      default:
        return `Error: ${type}`;
    }
  };

  return (
    <FormContainer onSubmit={handleSubmit(onSubmit)}>
      <GuideTextContainer>
        <GuideTitle variant="h5">
          Please update your password
        </GuideTitle>
        <GuideBody variant="caption">
          Your account is currently using a temporary password.{' '}
          Please enter a new password to secure your account.
        </GuideBody>
      </GuideTextContainer>
      <CollapsableNotification
        open={errorOpen}
        close={() => setErrorOpen(false)}
        message={errorMessage}
      />
      <StyledTextField
        placeholder="New Password"
        type="password"
        {...register('password', { required: true })}
        error={!!errors.password}
        errorMessage={errors.password ? getErrorMessage(errors.password.type, 'new password') : ''}
      />
      <ButtonContainer>
        <Button type="submit" loading={loading}>
          Set Password
        </Button>
      </ButtonContainer>
    </FormContainer>
  );
};

export default NewPasswordForm;
